/**
 * docs/UX.md 13 · `/m/batches`
 *
 * 教师 gets the simplified 模板 → 一次确认 flow and never sees the 新建批次
 * wizard (which needs `GET /materials` scoped to 课程团队 anyway); 课程团队 /
 * 管理员 keep the full wizard and get the template flow as a second button.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TASKS, batchDispatchSchema } from "@xinghai/shared";
import type { BatchCreatePayload, BatchDispatchPayload, Task } from "@xinghai/shared";
import {
  Button,
  EmptyState,
  Field,
  Icon,
  Input,
  ProgressBar,
  Select,
  Sheet,
  Table,
  useConfirmSafe,
  useToast,
} from "@xinghai/ui";
import type { Column } from "@xinghai/ui";
import { ApiError, batchesApi, materialsApi, speakersApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import { sortRoles, useActiveRole } from "../../app/roles";
import { useAuth } from "../auth/useAuth";
import { PublishToClassFlow } from "./PublishToClassFlow";
import {
  CheckboxPicker,
  ErrorNotice,
  KeyValue,
  LoadingBlock,
  PageHeader,
  QueryBoundary,
  Section,
  StatusTag,
} from "./common";
import {
  BATCH_STATE_ICON,
  BATCH_STATE_LABEL,
  BATCH_STATE_TONE,
  DISPATCH_STRATEGIES,
  TASK_LABEL,
} from "./labels";
import type { AssignPreview, BatchListItem, BatchSpeakerProgress, RetestPreview } from "./types";

/**
 * Route entry. Splits on the *active* role rather than on the raw role list so
 * a 教师+课程团队 account sees whichever screen matches the身份 they switched to.
 */
export function BatchesPage() {
  const { roles, isLoading } = useAuth();
  const [activeRole] = useActiveRole(sortRoles(roles));
  const isStaff = roles.includes("course_team") || roles.includes("admin");

  if (isLoading) {
    return (
      <div className="xz-adm">
        <PageHeader title="发布任务" />
        <LoadingBlock rows={3} label="正在加载" />
      </div>
    );
  }
  if (activeRole === "teacher" || !isStaff) return <TeacherPublishPage />;
  return <StaffBatchesPage />;
}

/** docs/UX.md 13 「教师简化流程」 — one screen, no wizard, no batch ids to type. */
function TeacherPublishPage() {
  const navigate = useNavigate();
  const list = useQuery({ queryKey: qk.batches, queryFn: ({ signal }) => batchesApi.list(signal) });
  const batches = list.data?.batches ?? [];
  // C1 —— 「发起第 1 次复测」直接开在这一屏上（docs/ROADMAP.md C1）
  const [retestFor, setRetestFor] = useState<BatchListItem | null>(null);

  return (
    <div className="xz-adm">
      <PageHeader
        title="发布任务"
        description="选一个模板，确认一次，全班学生的手机上就会出现新任务。"
      />
      <PublishToClassFlow mode="inline" onClose={() => navigate("/m")} />

      {retestFor && <RetestSheet batch={retestFor} onClose={() => setRetestFor(null)} />}

      <Section title="已经发布的任务" subtitle="学生的完成情况">
        <QueryBoundary
          isPending={list.isPending}
          isError={list.isError}
          error={list.error}
          onRetry={() => void list.refetch()}
        >
          {batches.length === 0 ? (
            <EmptyState title="还没有发布过任务" description="上面选一个模板，确认一下就可以了。" />
          ) : (
            <ul className="xz-adm__proglist">
              {batches.map((b) => (
                <li key={b.id} className="xz-adm__progitem">
                  <span>
                    {b.tasks.map((t) => TASK_LABEL[t]).join(" · ")}
                    {(b.retest_round ?? 0) > 0 && (
                      <span className="gz-caption"> · 第 {b.retest_round} 次复测</span>
                    )}
                  </span>
                  <span className="gz-caption">
                    {b.recorded_count}/{b.assignment_count}
                  </span>
                  <ProgressBar
                    size="sm"
                    value={b.assignment_count === 0 ? 0 : b.recorded_count / b.assignment_count}
                    label={`${b.id} 采集进度 ${b.recorded_count}/${b.assignment_count}`}
                    tone={
                      b.assignment_count > 0 && b.recorded_count >= b.assignment_count
                        ? "success"
                        : "info"
                    }
                  />
                  <Link className="gz-button xz-button--sm" to={`/m/batches/${b.id}`}>
                    完成情况
                  </Link>
                  {canRetest(b) && (
                    <Button size="sm" variant="secondary" onClick={() => setRetestFor(b)}>
                      发起第 1 次复测
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </QueryBoundary>
      </Section>
    </div>
  );
}

function StaffBatchesPage() {
  const toast = useToast();
  const confirm = useConfirmSafe();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const selected = params.get("batch");
  const [createOpen, setCreateOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [dispatchFor, setDispatchFor] = useState<BatchListItem | null>(null);
  // B4 派发式标注 / C1 复测轮次向导
  const [assignFor, setAssignFor] = useState<BatchListItem | null>(null);
  const [retestFor, setRetestFor] = useState<BatchListItem | null>(null);

  const list = useQuery({ queryKey: qk.batches, queryFn: ({ signal }) => batchesApi.list(signal) });

  const publish = useMutation({
    mutationFn: (id: string) => batchesApi.publish(id),
    onSuccess: (r) => {
      toast.success(`批次 ${r.batch.id} 已发布`);
      void qc.invalidateQueries({ queryKey: qk.batches });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onPublish = async (b: BatchListItem) => {
    const ok = await confirm({
      title: `发布批次 ${b.id}？`,
      message: "发布之后这个批次就可以派发给学生，批次内容不宜再大改。确认发布吗？",
      confirmLabel: "发布",
    });
    if (ok) publish.mutate(b.id);
  };

  const columns: Array<Column<BatchListItem>> = [
    {
      key: "id",
      header: "批次",
      render: (b) => <span className="xz-adm__mono">{b.id}</span>,
      width: "10rem",
    },
    {
      key: "phase",
      header: "阶段",
      render: (b) => (
        <span>
          {b.phase}
          {b.calibration && <span className="gz-caption"> · 校准批</span>}
          {(b.retest_round ?? 0) > 0 && (
            <span className="gz-caption"> · 第 {b.retest_round} 次复测</span>
          )}
        </span>
      ),
    },
    {
      key: "state",
      header: "状态",
      render: (b) => (
        <StatusTag icon={BATCH_STATE_ICON[b.state]} tone={BATCH_STATE_TONE[b.state]}>
          {BATCH_STATE_LABEL[b.state]}
        </StatusTag>
      ),
    },
    {
      key: "tasks",
      header: "任务",
      hideOnMobile: true,
      render: (b) => b.tasks.map((t) => TASK_LABEL[t]).join(" · "),
    },
    { key: "materials", header: "素材", align: "end", render: (b) => b.material_ids.length },
    {
      key: "progress",
      header: "采集进度",
      render: (b) => (
        <span>
          {b.recorded_count}/{b.assignment_count}
        </span>
      ),
    },
    {
      key: "actions",
      header: "操作",
      render: (b) => (
        <span className="xz-adm__rowactions">
          <Button size="sm" variant="secondary" onClick={() => setParams({ batch: b.id })}>
            详情
          </Button>
          <Link className="gz-button xz-button--sm" to={`/m/batches/${b.id}`}>
            完成情况
          </Link>
          {b.state === "draft" && (
            <Button size="sm" loading={publish.isPending} onClick={() => void onPublish(b)}>
              发布
            </Button>
          )}
          {b.state !== "draft" && b.state !== "frozen" && (
            <Button size="sm" onClick={() => setDispatchFor(b)}>
              派发
            </Button>
          )}
          {b.state !== "draft" && b.state !== "frozen" && (
            <Button size="sm" variant="secondary" onClick={() => setAssignFor(b)}>
              分配标注
            </Button>
          )}
          {canRetest(b) && (
            <Button size="sm" variant="secondary" onClick={() => setRetestFor(b)}>
              复测
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="xz-adm">
      <PageHeader
        title="批次与派发"
        description="创建批次 → 发布 → 按方言先验或手动派发。T5↔T6 成对素材会自动配对。"
        actions={
          <>
            <Button
              leadingIcon={<Icon id="ui-tag" size={16} />}
              onClick={() => setCreateOpen(true)}
            >
              新建批次
            </Button>
            <Button
              variant="secondary"
              leadingIcon={<Icon id="ui-upload" size={16} />}
              onClick={() => setTemplateOpen(true)}
            >
              发布任务给全班
            </Button>
          </>
        }
      />

      <QueryBoundary
        isPending={list.isPending}
        isError={list.isError}
        error={list.error}
        onRetry={() => void list.refetch()}
      >
        <Table
          caption="批次列表"
          columns={columns}
          rows={list.data?.batches ?? []}
          rowKey={(b) => b.id}
          empty={
            <EmptyState
              title="还没有批次"
              description="新建一个批次，选好任务和素材，再发布并派发。"
              action={<Button onClick={() => setCreateOpen(true)}>新建批次</Button>}
            />
          }
        />
      </QueryBoundary>

      {selected && <BatchDetailSheet id={selected} onClose={() => setParams({})} />}
      <BatchCreateSheet open={createOpen} onClose={() => setCreateOpen(false)} />
      {templateOpen && (
        <PublishToClassFlow onClose={() => setTemplateOpen(false)} doneHref="/m/batches" />
      )}
      {dispatchFor && <DispatchSheet batch={dispatchFor} onClose={() => setDispatchFor(null)} />}
      {assignFor && <AssignAnnotatorsSheet batch={assignFor} onClose={() => setAssignFor(null)} />}
      {retestFor && <RetestSheet batch={retestFor} onClose={() => setRetestFor(null)} />}
    </div>
  );
}

/**
 * C1 —— 复测只从**基线**批次发起（`retest_round === 0`），而且得先派发过、
 * 还没冻结，否则「同一批学生、同一批素材」无从谈起。
 */
export function canRetest(b: BatchListItem): boolean {
  if (b.calibration) return false;
  if ((b.retest_round ?? 0) !== 0) return false;
  if (b.state === "draft" || b.state === "frozen") return false;
  return b.assignment_count > 0;
}

// --------------------------------------------------------------------------- //
// detail — per-speaker 派发进度
// --------------------------------------------------------------------------- //

function deriveProgress(
  speakers: BatchSpeakerProgress[] | undefined,
  assignments: Array<{ speaker_id: string; state: string }> | undefined,
): BatchSpeakerProgress[] {
  if (speakers && speakers.length > 0) return speakers;
  if (!assignments) return [];
  const map = new Map<string, BatchSpeakerProgress>();
  for (const a of assignments) {
    const row = map.get(a.speaker_id) ?? { speaker_id: a.speaker_id, recorded: 0, required: 0 };
    row.required += 1;
    if (a.state === "recorded") row.recorded += 1;
    map.set(a.speaker_id, row);
  }
  return [...map.values()].sort((x, y) => x.speaker_id.localeCompare(y.speaker_id));
}

function BatchDetailSheet({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery({ queryKey: qk.batch(id), queryFn: () => batchesApi.get(id) });
  const d = q.data;
  const progress = useMemo(() => deriveProgress(d?.speakers, d?.assignments), [d]);

  return (
    <Sheet
      open
      onClose={onClose}
      title={`批次 ${id}`}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          关闭
        </Button>
      }
    >
      {q.isPending ? (
        <LoadingBlock rows={2} label="正在加载批次详情" />
      ) : q.isError || !d ? (
        <ErrorNotice error={q.error} onRetry={() => void q.refetch()} title="批次详情没能加载" />
      ) : (
        <div className="xz-stack">
          <KeyValue
            items={[
              { key: "phase", label: "阶段", value: d.batch.phase },
              {
                key: "state",
                label: "状态",
                value: (
                  <StatusTag
                    icon={BATCH_STATE_ICON[d.batch.state]}
                    tone={BATCH_STATE_TONE[d.batch.state]}
                  >
                    {BATCH_STATE_LABEL[d.batch.state]}
                  </StatusTag>
                ),
              },
              {
                key: "tasks",
                label: "任务",
                value: d.batch.tasks.map((t) => TASK_LABEL[t]).join(" · "),
              },
              { key: "materials", label: "素材数", value: d.materials.length },
              { key: "assignments", label: "派发条数", value: d.assignment_count },
              { key: "recorded", label: "已录", value: d.recorded_count },
            ]}
          />

          <div>
            <h3 className="gz-type-heading">整体采集进度</h3>
            <ProgressBar
              value={d.assignment_count === 0 ? 0 : d.recorded_count / d.assignment_count}
              label={`${id} 采集进度`}
              showLabel
            />
          </div>

          <div>
            <h3 className="gz-type-heading">按说话人的进度</h3>
            {progress.length === 0 ? (
              <p className="gz-muted">
                这个批次还没有派发记录；派发之后这里会按说话人显示「已录 / 应录」。
              </p>
            ) : (
              <ul className="xz-adm__proglist">
                {progress.map((p) => (
                  <li key={p.speaker_id} className="xz-adm__progitem">
                    <span className="xz-adm__mono">{p.speaker_id}</span>
                    <span className="gz-caption">
                      {p.recorded}/{p.required}
                    </span>
                    <ProgressBar
                      size="sm"
                      value={p.required === 0 ? 0 : p.recorded / p.required}
                      label={`${p.speaker_id} 录音进度 ${p.recorded}/${p.required}`}
                      tone={p.recorded >= p.required ? "success" : "info"}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {d.pairing && d.pairing.assignments > 0 && (
            <div>
              <h3 className="gz-type-heading">标注分配</h3>
              <p className="gz-caption">
                {d.pairing.assigned_recordings} 条录音已分配（共 {d.pairing.assignments} 人次）
                {d.pairing.teacher_overlap > 0 &&
                  `；其中 ${d.pairing.teacher_overlap} 人次躲不开师生重叠，已标记`}
                。已分配的录音只有被分配的专家看得到。
              </p>
              <ul className="xz-adm__proglist">
                {d.pairing.by_annotator.map((a) => (
                  <li key={a.annotator_id} className="xz-adm__progitem">
                    <span>{a.display_name}</span>
                    <span className="gz-caption">
                      {a.submitted}/{a.assigned}
                    </span>
                    <ProgressBar
                      size="sm"
                      value={a.assigned === 0 ? 0 : a.submitted / a.assigned}
                      label={`${a.display_name} 标注进度 ${a.submitted}/${a.assigned}`}
                      tone={a.submitted >= a.assigned ? "success" : "info"}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h3 className="gz-type-heading">素材（{d.materials.length}）</h3>
            <ul className="xz-issues">
              {d.materials.map((m) => (
                <li key={m.id} className="xz-issues__item">
                  <span className="xz-issues__where">{m.id}</span>
                  <span>
                    {m.title} · {TASK_LABEL[m.task]}
                    {m.pair_group ? ` · 配对组 ${m.pair_group}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Sheet>
  );
}

// --------------------------------------------------------------------------- //
// create
// --------------------------------------------------------------------------- //

function BatchCreateSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [id, setId] = useState("");
  const [phase, setPhase] = useState("P0");
  const [tasks, setTasks] = useState<string[]>([]);
  const [materialIds, setMaterialIds] = useState<string[]>([]);

  const materials = useQuery({
    queryKey: qk.materials({ all: true }),
    queryFn: ({ signal }) => materialsApi.list({}, signal),
    enabled: open,
  });

  const options = useMemo(
    () =>
      (materials.data?.materials ?? [])
        .filter((m) => tasks.length === 0 || tasks.includes(m.task))
        .map((m) => ({
          value: m.id,
          label: `${m.id} · ${m.title}`,
          hint: `${TASK_LABEL[m.task]} · ${m.text.slice(0, 18)}`,
        })),
    [materials.data, tasks],
  );

  const create = useMutation({
    mutationFn: (p: BatchCreatePayload) => batchesApi.create(p),
    onSuccess: (r) => {
      toast.success(`批次 ${r.batch.id} 已创建（草稿）`);
      void qc.invalidateQueries({ queryKey: qk.batches });
      setId("");
      setTasks([]);
      setMaterialIds([]);
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const valid =
    id.trim() !== "" && phase.trim() !== "" && tasks.length > 0 && materialIds.length > 0;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="新建批次"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button
            disabled={!valid}
            loading={create.isPending}
            onClick={() =>
              create.mutate({
                id: id.trim(),
                phase: phase.trim(),
                tasks: tasks as Task[],
                material_ids: materialIds,
              })
            }
          >
            创建
          </Button>
        </>
      }
    >
      <div className="xz-form">
        <div className="xz-form__row">
          <Field label="批次编号" required hint="例如 P0-T2">
            <Input value={id} onChange={(e) => setId(e.target.value)} placeholder="P0-T2" />
          </Field>
          <Field label="阶段" required hint="P0 / P1 / P2">
            <Input value={phase} onChange={(e) => setPhase(e.target.value)} />
          </Field>
        </div>

        <CheckboxPicker
          label="任务类型（可多选）"
          options={TASKS.map((t) => ({ value: t, label: TASK_LABEL[t] }))}
          value={tasks}
          onChange={setTasks}
        />

        {materials.isPending ? (
          <LoadingBlock rows={2} label="正在加载素材" />
        ) : materials.isError ? (
          <ErrorNotice
            error={materials.error}
            onRetry={() => void materials.refetch()}
            title="素材没能加载"
          />
        ) : (
          <CheckboxPicker
            label="素材（可多选）"
            searchable
            searchPlaceholder="搜索素材编号、标题或正文"
            options={options}
            value={materialIds}
            onChange={setMaterialIds}
            emptyText="没有匹配的素材；先选任务类型试试。"
          />
        )}
      </div>
    </Sheet>
  );
}

// --------------------------------------------------------------------------- //
// B4 派发式标注 —— 「分配标注」：预览每位专家的负载 → 确认
// --------------------------------------------------------------------------- //

/**
 * 两次点击：打开就自动跑一次 `dry_run` 预览（写不了任何东西），下面列出每位专家
 * 「现有 → 新增 → 合计」，确认才真的写。自动均衡尊重专家方向，能躲开说话人的
 * 任课教师就躲开；躲不开照旧允许，但会在这里明说有多少条被标记了。
 */
export function AssignAnnotatorsSheet({
  batch,
  onClose,
}: {
  batch: BatchListItem;
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const calibration = !!batch.calibration;
  const [mode, setMode] = useState<"auto" | "manual">("auto");
  const [pair, setPair] = useState<[string, string]>(["", ""]);
  const [preview, setPreview] = useState<AssignPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * 手动配对的候选人来自**第一次自动预览**：它已经列出了全部在岗专家和各自的
   * 负载。这样课程团队 / 教师不必有 `admin/users` 的权限也能点名（AUTHZ.md
   * 账号管理只归管理员）。
   */
  const [candidates, setCandidates] = useState<AssignPreview["per_annotator"]>([]);

  const payload = useMemo(
    () => ({
      mode,
      ...(mode === "manual" && pair[0] && pair[1] ? { annotator_ids: [pair[0], pair[1]] } : {}),
    }),
    [mode, pair],
  );

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      batchesApi.assignAnnotators(batch.id, {
        ...payload,
        dry_run: dryRun,
      } as Parameters<typeof batchesApi.assignAnnotators>[1]),
    onSuccess: (r) => {
      setPreview(r.preview);
      setError(null);
      if (r.preview.mode !== "manual" && r.preview.per_annotator.length > 0) {
        setCandidates(r.preview.per_annotator);
      }
      if (!r.applied) return;
      toast.success(
        `已分配 ${r.preview.recordings} 条录音（${r.preview.assignments} 人次）` +
          (r.preview.teacher_overlap > 0
            ? `；${r.preview.teacher_overlap} 条躲不开师生重叠，已标记`
            : ""),
      );
      void qc.invalidateQueries({ queryKey: qk.batch(batch.id) });
      void qc.invalidateQueries({ queryKey: qk.batches });
      onClose();
    },
    onError: (e: Error) => {
      setPreview(null);
      setError(e.message);
    },
  });

  // 打开即预览（docs/UX.md：打开就知道下一步）
  useEffect(() => {
    run.mutate(true);
    // 只在打开时跑一次；换模式由下面的 onChange 再触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const manualReady = mode !== "manual" || (!!pair[0] && !!pair[1] && pair[0] !== pair[1]);
  const nothingToDo = !!preview && preview.recordings === 0;

  return (
    <Sheet
      open
      onClose={onClose}
      title={`分配标注 · ${batch.id}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button
            disabled={!manualReady || nothingToDo}
            loading={run.isPending}
            onClick={() => run.mutate(false)}
          >
            确认分配{preview ? ` ${preview.recordings} 条` : ""}
          </Button>
        </>
      }
    >
      <div className="xz-form">
        {calibration ? (
          <div className="gz-notice" role="status">
            <Icon id="ui-info" size={20} />
            <span>
              这是<strong>校准批</strong>：每条录音都会发给全部在岗标注专家，结果只用来算
              周度一致性，不参与合并，也永远不会进入导出。
            </span>
          </div>
        ) : (
          <fieldset className="xz-form__choices">
            <legend>配对方式</legend>
            {(
              [
                {
                  value: "auto" as const,
                  label: "自动均衡",
                  hint: "按当前未完成负载轮转，尊重专家方向，尽量避开说话人的任课教师。",
                },
                {
                  value: "manual" as const,
                  label: "手动指定一对",
                  hint: "自己点名 E1 与 E2，整批都用这一对。",
                },
              ] as const
            ).map((s) => (
              <label key={s.value} className="xz-form__choice">
                <input
                  type="radio"
                  name="assign-mode"
                  value={s.value}
                  checked={mode === s.value}
                  onChange={() => {
                    setMode(s.value);
                    if (s.value === "auto") run.mutate(true);
                  }}
                />
                <span>
                  <span>{s.label}</span>
                  <span className="gz-caption">{s.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}

        {!calibration && mode === "manual" && (
          <div className="xz-form__row">
            {([0, 1] as const).map((i) => (
              <Field key={i} label={i === 0 ? "E1" : "E2"} required>
                <Select
                  value={pair[i]}
                  onChange={(e) => {
                    const next: [string, string] = [...pair] as [string, string];
                    next[i] = e.target.value;
                    setPair(next);
                    if (next[0] && next[1] && next[0] !== next[1]) {
                      setTimeout(() => run.mutate(true), 0);
                    }
                  }}
                >
                  <option value="">请选择</option>
                  {candidates.map((cand) => (
                    <option key={cand.annotator_id} value={cand.annotator_id}>
                      {cand.display_name}
                    </option>
                  ))}
                </Select>
              </Field>
            ))}
          </div>
        )}

        {error && (
          <div className="gz-notice gz-notice--error" role="alert">
            <Icon id="ui-error" size={20} />
            <span>{error}</span>
          </div>
        )}

        {preview && (
          <>
            <div className="gz-notice" role="status">
              <Icon id="ui-info" size={20} />
              <span>
                将分配 <strong>{preview.recordings}</strong> 条录音（
                {preview.assignments} 人次）
                {preview.already_assigned > 0 && `；${preview.already_assigned} 条已经分配过，跳过`}
                {preview.skipped.length > 0 && `；${preview.skipped.length} 条人手不够，跳过`}
                {preview.teacher_overlap > 0 &&
                  `；${preview.teacher_overlap} 条躲不开师生重叠，会被标记`}
                。分配之后，这些录音只有被分配的专家看得到。
              </span>
            </div>

            <div>
              <h3 className="gz-type-heading">每位专家的负载</h3>
              <ul className="xz-adm__proglist">
                {preview.per_annotator.map((a) => (
                  <li key={a.annotator_id} className="xz-adm__progitem">
                    <span>{a.display_name}</span>
                    <span className="gz-caption">
                      现有 {a.current} + 新增 {a.added} = {a.current + a.added}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {nothingToDo && (
          <p className="gz-muted">
            这个批次里没有可以分配的录音（要先通过质检，而且还没被分配过）。
          </p>
        )}
      </div>
    </Sheet>
  );
}

// --------------------------------------------------------------------------- //
// C1 复测轮次向导 —— 选轮次 → 看将创建什么 → 确认
// --------------------------------------------------------------------------- //

/** 2–3 次点击：选第 1 / 第 2 轮 → 预览 → 确认。 */
export function RetestSheet({ batch, onClose }: { batch: BatchListItem; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [round, setRound] = useState<1 | 2>(1);
  const [preview, setPreview] = useState<RetestPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: (vars: { round: 1 | 2; dryRun: boolean }) =>
      batchesApi.retest(batch.id, { round: vars.round, dry_run: vars.dryRun }),
    onSuccess: (r) => {
      setPreview(r.preview);
      setError(null);
      if (!r.applied) return;
      toast.success(`已生成复测批次 ${r.preview.batch_id}，共 ${r.assignments_created} 条任务`);
      void qc.invalidateQueries({ queryKey: qk.batches });
      onClose();
    },
    onError: (e: Error) => {
      setPreview(null);
      setError(e.message);
    },
  });

  useEffect(() => {
    run.mutate({ round, dryRun: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);

  return (
    <Sheet
      open
      onClose={onClose}
      title={`复测 · ${batch.id}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button
            disabled={!preview || preview.exists || preview.speakers === 0}
            loading={run.isPending}
            onClick={() => run.mutate({ round, dryRun: false })}
          >
            确认发起第 {round} 次复测
          </Button>
        </>
      }
    >
      <div className="xz-form">
        <fieldset className="xz-form__choices">
          <legend>复测轮次</legend>
          {([1, 2] as const).map((r) => (
            <label key={r} className="xz-form__choice">
              <input
                type="radio"
                name="retest-round"
                value={r}
                checked={round === r}
                onChange={() => setRound(r)}
              />
              <span>
                <span>第 {r} 次复测</span>
                <span className="gz-caption">{r === 1 ? "第 4 周" : "第 8 周"}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {error && (
          <div className="gz-notice gz-notice--error" role="alert">
            <Icon id="ui-error" size={20} />
            <span>{error}</span>
          </div>
        )}

        {preview && (
          <>
            <KeyValue
              items={[
                { key: "source", label: "基线批次", value: preview.source_title },
                { key: "id", label: "新批次编号", value: preview.batch_id },
                { key: "title", label: "新批次名称", value: preview.title },
                { key: "speakers", label: "学生", value: `${preview.speakers} 人` },
                { key: "materials", label: "素材", value: `${preview.materials} 条` },
                { key: "assignments", label: "将生成任务", value: `${preview.assignments} 条` },
              ]}
            />
            <div className="gz-notice" role="status">
              <Icon id="ui-info" size={20} />
              <span>
                同一批学生、同一批素材，每人拿回的还是他自己那一份。复测不会覆盖基线，
                两轮数据各自独立保存。
                {preview.skipped_no_consent.length > 0 &&
                  ` ${preview.skipped_no_consent.length} 位学生授权不完整，会被跳过。`}
              </span>
            </div>
            {preview.exists && (
              <div className="gz-notice gz-notice--error" role="alert">
                <Icon id="ui-warning" size={20} />
                <span>批次 {preview.batch_id} 已经存在 —— 这一轮复测发过了，不要再发一遍。</span>
              </div>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}

// --------------------------------------------------------------------------- //
// dispatch wizard
// --------------------------------------------------------------------------- //

type Strategy = "manual" | "dialect_prior" | "round_robin";

export function DispatchSheet({ batch, onClose }: { batch: BatchListItem; onClose: () => void }) {
  const toast = useToast();
  const confirm = useConfirmSafe();
  const qc = useQueryClient();
  const [speakerIds, setSpeakerIds] = useState<string[]>([]);
  const [strategy, setStrategy] = useState<Strategy>("manual");
  const [materialIds, setMaterialIds] = useState<string[]>(batch.material_ids);
  const [pair, setPair] = useState(true);
  const [retest, setRetest] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);

  const speakers = useQuery({
    queryKey: qk.speakersFiltered({}),
    queryFn: ({ signal }) => speakersApi.list({}, signal),
  });

  useEffect(() => setFormError(null), [speakerIds, strategy, materialIds]);

  const speakerOptions = useMemo(
    () =>
      (speakers.data?.speakers ?? []).map((s) => ({
        value: s.id,
        label: `${s.id}${s.display_name ? ` · ${s.display_name}` : ""}`,
        hint: [
          s.class_id ?? "未分班",
          s.dialect_l1.join("/") || "方言未填",
          s.consent_complete ? "授权齐全" : "授权未全签，将被跳过",
        ].join(" · "),
        group: s.class_id,
        disabled: !s.consent_complete,
      })),
    [speakers.data],
  );

  const effectiveMaterials =
    strategy === "manual" ? materialIds : materialIds.length > 0 ? materialIds : batch.material_ids;
  const previewCount = speakerIds.length * effectiveMaterials.length;

  const run = useMutation({
    mutationFn: (p: BatchDispatchPayload) => batchesApi.dispatch(batch.id, p),
    onSuccess: (r) => {
      const skipped = r.dispatch.skipped_no_consent.length;
      toast.success(
        `已派发 ${r.dispatch.assignments_created} 条（${r.dispatch.speakers} 位说话人 · ${r.dispatch.pairs} 组配对）` +
          (skipped > 0 ? `；${skipped} 位因授权未全签被跳过` : ""),
      );
      void qc.invalidateQueries({ queryKey: qk.batches });
      void qc.invalidateQueries({ queryKey: qk.batch(batch.id) });
      onClose();
    },
    onError: async (e: Error, sent) => {
      // 409 DUPLICATE_BASELINE — these speakers already hold these materials at
      // this retest round in another live batch. Offer the repeat behind a
      // confirm instead of a bare toast (see services/dispatch.ts).
      if (e instanceof ApiError && e.code === "DUPLICATE_BASELINE") {
        const ok = await confirm({
          title: "这些学生已经有同样的任务了",
          message: `${e.message} 确认后会重复派发，学生会看到两份一样的内容。`,
          confirmLabel: "仍然派发",
          danger: true,
        });
        if (ok) run.mutate({ ...sent, allow_duplicate: true });
        return;
      }
      toast.error(e.message);
    },
  });

  /** Builds and validates the exact `batchDispatchSchema` payload before sending. */
  const submit = () => {
    const draft = {
      speaker_ids: speakerIds,
      ...(materialIds.length > 0 ? { material_ids: materialIds } : {}),
      strategy,
      pair_t5_t6: pair,
      retest_round: retest,
    };
    const parsed = batchDispatchSchema.safeParse(draft);
    if (!parsed.success) {
      setFormError(parsed.error.issues.map((i) => i.message).join("；"));
      return;
    }
    run.mutate(parsed.data);
  };

  const strategyHint = DISPATCH_STRATEGIES.find((s) => s.value === strategy)?.hint ?? "";

  return (
    <Sheet
      open
      onClose={onClose}
      title={`派发批次 ${batch.id}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button disabled={speakerIds.length === 0} loading={run.isPending} onClick={submit}>
            派发 {previewCount > 0 ? `${previewCount} 条` : ""}
          </Button>
        </>
      }
    >
      <div className="xz-form">
        {speakers.isPending ? (
          <LoadingBlock rows={2} label="正在加载说话人" />
        ) : speakers.isError ? (
          <ErrorNotice
            error={speakers.error}
            onRetry={() => void speakers.refetch()}
            title="说话人没能加载"
          />
        ) : (
          <CheckboxPicker
            label="选择说话人"
            options={speakerOptions}
            value={speakerIds}
            onChange={setSpeakerIds}
            searchable
            searchPlaceholder="搜索说话人编号、姓名或方言"
            groupLabel="班级"
            emptyText="没有可派发的说话人。"
          />
        )}

        <fieldset className="xz-form__choices">
          <legend>派发策略</legend>
          {DISPATCH_STRATEGIES.map((s) => (
            <label key={s.value} className="xz-form__choice">
              <input
                type="radio"
                name="dispatch-strategy"
                value={s.value}
                checked={strategy === s.value}
                onChange={() => setStrategy(s.value)}
              />
              <span>
                <span>{s.label}</span>
                <span className="gz-caption">{s.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <CheckboxPicker
          label={strategy === "manual" ? "素材（手动派发必选）" : "素材（留空则用批次自带素材）"}
          options={batch.material_ids.map((m) => ({ value: m, label: m }))}
          value={materialIds}
          onChange={setMaterialIds}
          searchable
          searchPlaceholder="搜索素材编号"
          emptyText="这个批次还没有素材。"
        />

        <label className="xz-form__choice">
          <input type="checkbox" checked={pair} onChange={(e) => setPair(e.target.checked)} />
          <span>
            <span>T5↔T6 成对派发</span>
            <span className="gz-caption">
              同一 pair_group 的歌词朗读与演唱绑定成对，演唱在朗读提交前锁定。
            </span>
          </span>
        </label>

        <Field label="复测轮次" hint="0 表示首轮；复测会再生成一份同样的派发，不覆盖上一轮。">
          <Input
            type="number"
            min={0}
            max={9}
            value={retest}
            onChange={(e) => setRetest(Math.max(0, Math.min(9, Number(e.target.value) || 0)))}
          />
        </Field>

        <div className="gz-notice" role="status">
          <Icon id="ui-info" size={20} />
          <span>
            预计生成 <strong>{previewCount}</strong> 条派发（{speakerIds.length} 位说话人 ×{" "}
            {effectiveMaterials.length} 条素材）。{strategyHint}
          </span>
        </div>

        {formError && (
          <div className="gz-notice gz-notice--error" role="alert">
            <Icon id="ui-error" size={20} />
            <span>{formError}</span>
          </div>
        )}
      </div>
    </Sheet>
  );
}
